/**
 * React hook for registering keyboard shortcuts in components
 *
 * Automatically handles registration/cleanup on mount/unmount.
 * Follows Google SPA patterns for common shortcuts.
 *
 * @example Gmail-style navigation
 * ```typescript
 * function EmailList() {
 *   useKeyboardShortcut('j', selectNext, {
 *     description: 'Select next email',
 *     category: 'Navigation'
 *   });
 *
 *   useKeyboardShortcut('k', selectPrevious, {
 *     description: 'Select previous email',
 *     category: 'Navigation'
 *   });
 * }
 * ```
 *
 * @example Google Docs-style save
 * ```typescript
 * function DocumentEditor() {
 *   useKeyboardShortcut('ctrl+s', handleSave, {
 *     description: 'Save document',
 *     category: 'Actions'
 *   });
 * }
 * ```
 *
 * @example Gmail-style compose
 * ```typescript
 * function Inbox() {
 *   useKeyboardShortcut('c', openCompose, {
 *     description: 'Compose new email',
 *     category: 'Actions',
 *     requireNotInput: true
 *   });
 * }
 * ```
 *
 * @example Go-to shortcuts (Gmail g+i pattern)
 * ```typescript
 * function Navigation() {
 *   useKeyboardShortcut('g+i', () => navigate('/inbox'), {
 *     description: 'Go to inbox',
 *     category: 'Navigation'
 *   });
 *
 *   useKeyboardShortcut('g+s', () => navigate('/starred'), {
 *     description: 'Go to starred',
 *     category: 'Navigation'
 *   });
 * }
 * ```
 *
 * @example Using standard shortcuts
 * ```typescript
 * import { SHORTCUTS } from '@/services/keyboard'
 *
 * function MyComponent() {
 *   useKeyboardShortcut(SHORTCUTS.SEARCH, focusSearch);
 *   useKeyboardShortcut(SHORTCUTS.HELP, showHelp);
 *   useKeyboardShortcut(SHORTCUTS.CANCEL, closeModal);
 *
 *   useKeyboardShortcut(SHORTCUTS.SAVE, handleSave);
 *   useKeyboardShortcut(SHORTCUTS.DELETE, handleDelete);
 *
 *   useKeyboardShortcut(SHORTCUTS.NAV.VIM.NEXT, selectNext);
 *   useKeyboardShortcut(SHORTCUTS.NAV.VIM.PREVIOUS, selectPrevious);
 * }
 * ```
 *
 * @example Conditional shortcuts
 * ```typescript
 * function Form() {
 *   const [isDirty, setIsDirty] = useState(false);
 *
 *   useKeyboardShortcut('ctrl+s', handleSave, {
 *     description: 'Save form',
 *     category: 'Actions',
 *     enabled: isDirty
 *   });
 * }
 * ```
 */

import { useEffect, useRef } from 'react';
import { useKeyboardShortcutContext } from '../services/keyboard/KeyboardShortcutContext';
import type { ShortcutPattern, ShortcutOptions } from '../services/keyboard/types';

/**
 * Register a keyboard shortcut for the lifetime of the component
 *
 * @param pattern - Shortcut pattern (e.g., 'ctrl+s', 'j', 'g+i')
 * @param callback - Function to execute when shortcut is triggered
 * @param options - Configuration options
 *
 * @remarks
 * The shortcut is automatically registered on mount and unregistered on unmount.
 * The callback is kept up-to-date via ref to avoid unnecessary re-registrations.
 */
export function useKeyboardShortcut(
  pattern: ShortcutPattern,
  callback: (event: KeyboardEvent) => void,
  options: ShortcutOptions = {}
): void {
  const { service } = useKeyboardShortcutContext();
  const callbackRef = useRef(callback);
  const idRef = useRef<string | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const optionsKey = JSON.stringify(options);
  useEffect(() => {
    const wrappedCallback = (event: KeyboardEvent) => {
      callbackRef.current(event);
    };

    idRef.current = service.register(pattern, wrappedCallback, options);

    return () => {
      if (idRef.current) {
        service.unregister(idRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options is intentionally serialized to optionsKey for stable identity; options ref itself may change each render
  }, [service, pattern, optionsKey]);
}
